import { useMemo } from 'react';
import { Popover } from '@mantine/core';
import { IoChevronDown, IoFilter } from 'react-icons/io5';
import { Button } from '@shared/ui';
import { ProductFilters } from '@components';
import type { ProductFiltersValues } from '@shared/components/ProductFilters';
import './FiltersPopover.css';

export interface FiltersPopoverProps {
    opened: boolean;
    onChange: (opened: boolean) => void;
    filterValues: ProductFiltersValues;
    onFilterChange: (values: ProductFiltersValues) => void;
    onClear: () => void;
    sellerSelectOptions?: { value: string; label: string }[];
    categorySelectOptions?: { value: string; label: string }[];
    appearance?: 'icon' | 'quiet';
}

export default function FiltersPopover({
    opened,
    onChange,
    filterValues,
    onFilterChange,
    onClear,
    sellerSelectOptions,
    categorySelectOptions,
    appearance = 'icon',
}: FiltersPopoverProps) {
    const activeFiltersCount = useMemo(() => {
        let count = 0;
        count += filterValues.colorIds.length;
        count += filterValues.flowerTypeIds.length;
        count += filterValues.assemblyTypeIds.length;
        if (filterValues.minPrice.trim()) count++;
        if (filterValues.maxPrice.trim()) count++;
        if (filterValues.sellerId?.trim()) count++;
        if (filterValues.categoryId?.trim()) count++;
        return count;
    }, [filterValues]);

    return (
        <Popover
            opened={opened}
            onChange={onChange}
            position="bottom-start"
            withArrow
            closeOnClickOutside={false}
            classNames={{ dropdown: 'filters-popover-dropdown' }}
        >
            <Popover.Target>
                {appearance === 'quiet' ? (
                    <button
                        type="button"
                        className={`filters-popover-trigger${activeFiltersCount > 0 ? ' filters-popover-trigger--active' : ''}${opened ? ' filters-popover-trigger--open' : ''}`}
                        aria-label="Фильтры товаров"
                        aria-expanded={opened}
                        onClick={() => onChange(!opened)}
                    >
                        <span>Фильтры</span>
                        {activeFiltersCount > 0 ? (
                            <span className="filters-popover-trigger-badge">{activeFiltersCount}</span>
                        ) : null}
                        <IoChevronDown size={14} aria-hidden />
                    </button>
                ) : (
                    <Button
                        variant="icon"
                        onClick={() => onChange(!opened)}
                        className="filters-popover-button"
                        aria-label="Открыть фильтры"
                    >
                        <IoFilter />
                        {activeFiltersCount > 0 && (
                            <span className="filters-popover-badge">
                                {activeFiltersCount}
                            </span>
                        )}
                    </Button>
                )}
            </Popover.Target>
            <Popover.Dropdown>
                <div className="filters-popover-content">
                    <ProductFilters
                        values={filterValues}
                        onChange={onFilterChange}
                        showSearch={false}
                        showClearButton={true}
                        onClear={onClear}
                        sellerSelectOptions={sellerSelectOptions}
                        categorySelectOptions={categorySelectOptions}
                    />
                </div>
            </Popover.Dropdown>
        </Popover>
    );
}
